/**
 * Fetch-based transport for non-streaming operations.
 *
 * This is intentionally universal so it can be used from browser
 * adapters and from tests without pulling in any React-only code.
 */

import type { CallTransport } from "./context/execution-context";

export interface FetchCallTransportOptions {
  baseUrl?: string;
  defaultHeaders?: () => Record<string, string>;
}

function normalizeBaseUrl(baseUrl: string | undefined): string {
  return String(baseUrl ?? "").trim().replace(/\/+$/, "");
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

/**
 * Derive a non-empty human-readable error message from an upstream response.
 *
 * Backends and proxies are not consistent about the error envelope key, so we
 * accept `error`, `message`, `detail`, and `errorMessage` before falling back
 * to the raw body text and finally a status-based message. The previous logic
 * only checked `data.error`, which produced an empty `error` string for envelopes
 * using `message`/`detail` and left callers logging useless `{}` metadata.
 */
function extractErrorMessage(
  data: unknown,
  statusText: string,
  status: number,
): string {
  if (isPlainObject(data)) {
    for (const key of ["error", "message", "detail", "errorMessage"]) {
      const value = data[key];
      if (typeof value === "string" && value.trim()) {
        return value.trim();
      }
    }
  }

  if (typeof data === "string" && data.trim()) {
    return data.trim();
  }

  const fallback = statusText && statusText.trim();
  return fallback || `Request failed with status ${status}`;
}

export function createFetchCallTransport(
  options: FetchCallTransportOptions = {},
): CallTransport {
  const baseUrl = normalizeBaseUrl(options.baseUrl);

  return {
    async call(operationId, payload, opts) {
      const url = `${baseUrl}/api/operations/${encodeURIComponent(operationId)}`;

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(options.defaultHeaders?.() ?? {}),
      };

      const init: RequestInit = {
        method: "POST",
        headers,
        body: JSON.stringify(payload ?? {}),
        credentials: "include",
      };

      if (opts?.signal) {
        init.signal = opts.signal;
      }

      try {
        const res = await fetch(url, init);
        const status = res.status;
        const text = await res.text();

        let data: unknown = null;
        if (text) {
          try {
            data = JSON.parse(text);
          } catch {
            data = text;
          }
        }

        if (!res.ok) {
          const error = extractErrorMessage(data, res.statusText, status);
          return { ok: false, data, error, status };
        }

        return { ok: true, data, status };
      } catch (err) {
        const message =
          err instanceof Error && err.message.trim()
            ? err.message.trim()
            : "Network error";
        return {
          ok: false,
          data: null,
          error: message,
          status: 0,
        };
      }
    },
  };
}